import { effectiveNoticeChannel, type NoticeChannel } from "./noticeChannel";

/** A translation key and its values, chosen here and translated by the UI. */
export interface TranslatableText {
    key: string;
    values?: Record<string, string | number>;
}

export interface NoticeSummaryInput {
    enabled: boolean;
    toPatient: boolean;
    toTherapist: boolean;
    /** The stored channel of the patient copy. */
    channel: NoticeChannel;
    /** Whether this deployment can send this notification on WhatsApp. */
    whatsappAvailable: boolean;
    /** Row-specific timing, supplied by the tool. */
    when: TranslatableText;
}

/** A sentence template plus its already-chosen fragments. */
export interface NoticeSummary {
    key: string;
    parts: Record<string, TranslatableText>;
}

// Literal keys, so the i18n parity guard can see every one of them.
const SUMMARY = {
    notSent: "notificationPreferences.summary.notSent",
    nobody: "notificationPreferences.summary.nobody",
    sentToPatient: "notificationPreferences.summary.sentToPatient",
    sentToTherapist: "notificationPreferences.summary.sentToTherapist",
    sentToBoth: "notificationPreferences.summary.sentToBoth",
    therapistByEmail: "notificationPreferences.summary.therapistByEmail",
} as const;

const PATIENT_BY: Record<NoticeChannel, string> = {
    email: "notificationPreferences.summary.patientByEmail",
    whatsapp: "notificationPreferences.summary.patientByWhatsApp",
    email_and_whatsapp: "notificationPreferences.summary.patientByEmailAndWhatsApp",
};

/**
 * The one line under a notification: what is sent, when, to whom and how.
 *
 * The patient part follows what the copy actually travels on, not what is
 * stored: a row the deployment cannot send on WhatsApp is described as email,
 * so the line never promises a message that will not go out. Whenever WhatsApp
 * does apply, its fragment carries the consent rule — the therapist's choice
 * only ever reaches patients who accepted WhatsApp themselves.
 */
export function summariseNotice(input: NoticeSummaryInput): NoticeSummary {
    if (!input.enabled) return { key: SUMMARY.notSent, parts: {} };

    const patient = {
        key: PATIENT_BY[effectiveNoticeChannel(input.channel, input.whatsappAvailable)],
    };
    const therapist = { key: SUMMARY.therapistByEmail };

    if (input.toPatient && input.toTherapist) {
        return { key: SUMMARY.sentToBoth, parts: { when: input.when, patient, therapist } };
    }
    if (input.toPatient) {
        return { key: SUMMARY.sentToPatient, parts: { when: input.when, patient } };
    }
    if (input.toTherapist) {
        return { key: SUMMARY.sentToTherapist, parts: { when: input.when, therapist } };
    }
    return { key: SUMMARY.nobody, parts: {} };
}
